/**
 * Blueprints & Catalog — Port-style entity-type registry.
 *
 * Left pane: list of blueprints.
 * Right pane: schema editor + link to this blueprint's entities.
 */
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Boxes, Plus, RefreshCw, Trash2, Save, ExternalLink, Database, AlertCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Property {
  name: string
  title?: string
  type: string
  enum?: string[] | null
  required?: boolean
  default?: any
  description?: string
}

interface Relation {
  identifier: string
  title?: string
  target_blueprint: string
  many?: boolean
}

interface Blueprint {
  id: number
  identifier: string
  title: string
  icon: string
  description: string
  schema: Property[]
  relations: Relation[]
  is_system: boolean
  created_at: string
  updated_at: string
  entity_count?: number
}

const PROP_TYPES = ['string', 'number', 'boolean', 'url', 'email', 'select', 'date', 'array']

export default function BlueprintsPage() {
  const [blueprints, setBlueprints] = useState<Blueprint[]>([])
  const [selected, setSelected] = useState<Blueprint | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/blueprints')
      setBlueprints(data.blueprints || [])
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load blueprints')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openBlueprint = async (identifier: string) => {
    try {
      const { data } = await api.get(`/api/blueprints/${identifier}`)
      setSelected(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to fetch blueprint')
    }
  }

  const seed = async () => {
    setSaving(true)
    try {
      await api.post('/api/blueprints/seed')
      await api.post('/api/scorecards/seed')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Seed failed')
    } finally {
      setSaving(false)
    }
  }

  const createNew = () => {
    setSelected({
      id: 0,
      identifier: '',
      title: '',
      icon: '',
      description: '',
      schema: [],
      relations: [],
      is_system: false,
      created_at: '',
      updated_at: '',
    })
  }

  const save = async () => {
    if (!selected) return
    if (!/^[a-z][a-z0-9_-]{1,79}$/.test(selected.identifier)) {
      setError('identifier must be lowercase slug (a-z 0-9 _ -)')
      return
    }
    setSaving(true)
    try {
      const payload = {
        identifier: selected.identifier,
        title: selected.title,
        icon: selected.icon,
        description: selected.description,
        schema: selected.schema,
        relations: selected.relations,
      }
      if (selected.id === 0) {
        await api.post('/api/blueprints', payload)
      } else {
        await api.put(`/api/blueprints/${selected.identifier}`, payload)
      }
      await load()
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const deleteBp = async () => {
    if (!selected || selected.id === 0) return
    if (!confirm(`Delete blueprint "${selected.identifier}" and all its entities?`)) return
    try {
      await api.delete(`/api/blueprints/${selected.identifier}`)
      setSelected(null)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Delete failed')
    }
  }

  const addProp = () => {
    if (!selected) return
    setSelected({
      ...selected,
      schema: [...selected.schema, { name: 'new_field', type: 'string', required: false }],
    })
  }

  const updateProp = (idx: number, patch: Partial<Property>) => {
    if (!selected) return
    const next = [...selected.schema]
    next[idx] = { ...next[idx], ...patch }
    setSelected({ ...selected, schema: next })
  }

  const removeProp = (idx: number) => {
    if (!selected) return
    setSelected({ ...selected, schema: selected.schema.filter((_, i) => i !== idx) })
  }

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <Database className="w-6 h-6 text-sky" />
              Blueprints & Catalog
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-1">
              Define custom entity types. Each blueprint produces a catalog of entities with scorecards.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={seed}
              disabled={saving}
              className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1.5"
            >
              <RefreshCw className={clsx('w-4 h-4', saving && 'animate-spin')} />
              Seed system blueprints
            </button>
            <button
              onClick={createNew}
              className="px-3 py-1.5 rounded bg-sky/20 hover:bg-sky/30 text-sky text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              New blueprint
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Left — list */}
          <div className="col-span-4">
            <div className="border border-gunmetal rounded overflow-hidden">
              <div className="bg-gunmetal/40 px-3 py-2 text-xs uppercase tracking-wider text-whitesmoke/60">
                Blueprints ({blueprints.length})
              </div>
              {loading && blueprints.length === 0 && (
                <div className="p-4 text-sm text-whitesmoke/60">Loading…</div>
              )}
              {!loading && blueprints.length === 0 && (
                <div className="p-6 text-center text-sm text-whitesmoke/50">
                  No blueprints yet. Click <strong>Seed system blueprints</strong> to create
                  Service, Cloud Resource and Team.
                </div>
              )}
              <ul>
                {blueprints.map(bp => (
                  <li key={bp.identifier}>
                    <button
                      onClick={() => openBlueprint(bp.identifier)}
                      className={clsx(
                        'w-full text-left px-3 py-2.5 border-b border-gunmetal/50 hover:bg-gunmetal/30 transition',
                        selected?.identifier === bp.identifier && 'bg-sky/10 border-l-2 border-l-sky',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Boxes className="w-4 h-4 text-whitesmoke/60" />
                        <span className="font-medium">{bp.title}</span>
                        {bp.is_system && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky/20 text-sky">SYSTEM</span>
                        )}
                      </div>
                      <div className="text-xs text-whitesmoke/50 mt-0.5 font-mono">
                        {bp.identifier}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — editor */}
          <div className="col-span-8">
            {!selected && (
              <div className="border border-gunmetal rounded p-12 text-center text-whitesmoke/40">
                Select a blueprint on the left or create a new one.
              </div>
            )}
            {selected && (
              <div className="border border-gunmetal rounded">
                <div className="bg-gunmetal/40 px-4 py-3 flex items-center justify-between">
                  <div className="text-sm uppercase tracking-wider text-whitesmoke/70">
                    {selected.id === 0 ? 'New blueprint' : 'Edit blueprint'}
                  </div>
                  <div className="flex gap-2">
                    {selected.id !== 0 && (
                      <Link
                        to={`/app/entities/${selected.identifier}`}
                        className="px-2.5 py-1 rounded bg-sky/10 hover:bg-sky/20 text-sky text-xs flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View entities ({selected.entity_count ?? 0})
                      </Link>
                    )}
                    {selected.id !== 0 && !selected.is_system && (
                      <button
                        onClick={deleteBp}
                        className="px-2.5 py-1 rounded bg-rose/10 hover:bg-rose/20 text-rose text-xs flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    )}
                    <button
                      onClick={save}
                      disabled={saving}
                      className="px-2.5 py-1 rounded bg-emerald/20 hover:bg-emerald/30 text-emerald text-xs flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-xs text-whitesmoke/60">Identifier (slug)</span>
                      <input
                        value={selected.identifier}
                        onChange={e => setSelected({ ...selected, identifier: e.target.value })}
                        disabled={selected.id !== 0}
                        className="w-full mt-1 px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm font-mono"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-whitesmoke/60">Title</span>
                      <input
                        value={selected.title}
                        onChange={e => setSelected({ ...selected, title: e.target.value })}
                        className="w-full mt-1 px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-xs text-whitesmoke/60">Description</span>
                    <textarea
                      value={selected.description}
                      onChange={e => setSelected({ ...selected, description: e.target.value })}
                      rows={2}
                      className="w-full mt-1 px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
                    />
                  </label>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider text-whitesmoke/70">
                        Schema — {selected.schema.length} properties
                      </span>
                      <button
                        onClick={addProp}
                        className="text-xs px-2 py-0.5 rounded bg-sky/10 hover:bg-sky/20 text-sky flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> add
                      </button>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="text-xs text-whitesmoke/60">
                        <tr className="border-b border-gunmetal/50">
                          <th className="text-left py-1.5 font-normal">Name</th>
                          <th className="text-left py-1.5 font-normal">Title</th>
                          <th className="text-left py-1.5 font-normal">Type</th>
                          <th className="text-left py-1.5 font-normal">Required</th>
                          <th className="text-left py-1.5 font-normal">Enum (csv)</th>
                          <th className="py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.schema.map((p, i) => (
                          <tr key={i} className="border-b border-gunmetal/30">
                            <td className="py-1">
                              <input
                                value={p.name}
                                onChange={e => updateProp(i, { name: e.target.value })}
                                className="w-full px-1.5 py-1 bg-graphite border border-gunmetal/50 rounded text-xs font-mono"
                              />
                            </td>
                            <td className="py-1">
                              <input
                                value={p.title || ''}
                                onChange={e => updateProp(i, { title: e.target.value })}
                                className="w-full px-1.5 py-1 bg-graphite border border-gunmetal/50 rounded text-xs"
                              />
                            </td>
                            <td className="py-1">
                              <select
                                value={p.type}
                                onChange={e => updateProp(i, { type: e.target.value })}
                                className="px-1.5 py-1 bg-graphite border border-gunmetal/50 rounded text-xs"
                              >
                                {PROP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td className="py-1">
                              <input
                                type="checkbox"
                                checked={!!p.required}
                                onChange={e => updateProp(i, { required: e.target.checked })}
                              />
                            </td>
                            <td className="py-1">
                              <input
                                value={(p.enum || []).join(',')}
                                onChange={e => updateProp(i, {
                                  enum: e.target.value ? e.target.value.split(',').map(s => s.trim()) : null,
                                })}
                                placeholder="val1,val2"
                                className="w-full px-1.5 py-1 bg-graphite border border-gunmetal/50 rounded text-xs"
                              />
                            </td>
                            <td className="py-1 text-right">
                              <button onClick={() => removeProp(i)} className="text-rose/70 hover:text-rose">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {selected.schema.length === 0 && (
                          <tr>
                            <td colSpan={6} className="py-4 text-center text-whitesmoke/40 text-xs">
                              No properties yet — click "add" to define one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
